import { authorizedFetch, json } from '@nimbitlabs/ui';

export type AlertSeverity = 'critical' | 'warning' | 'info';
export type AlertChannel = 'slack' | 'email' | 'webhook';
export type AlertStatus = 'active' | 'acknowledged' | 'resolved';

export interface NotificationTarget {
  channel: AlertChannel;
  recipients: string[]; // email addresses, slack channels, or webhook URLs
}

export interface AlertRule {
  id: string;
  name: string;
  serviceId: string;
  environmentId: string;
  condition: string;
  threshold: number;
  severity: AlertSeverity;
  notificationTargets: NotificationTarget[];
  enabled: boolean;
  autoCreateIncident: boolean;
  incidentEscalationThreshold: number;
  criticalEscalationThreshold: number;
  createdAt: Date;
}

export interface Alert {
  id: string;
  ruleId: string;
  ruleName: string;
  serviceId: string;
  serviceName: string;
  environmentId: string;
  environmentName: string;
  severity: AlertSeverity;
  message: string;
  status: AlertStatus;
  triggeredAt: Date;
  acknowledgedAt?: Date;
  acknowledgedBy?: string;
  resolvedAt?: Date;
}

export interface OnCallSchedule {
  id: string;
  teamId: string;
  userId: string;
  userName: string;
  startTime: Date;
  endTime: Date;
  isActive: boolean;
}

// Date parsing helpers
const parseAlertRuleDate = (data: any): AlertRule => ({
  ...data,
  createdAt: new Date(data.createdAt),
});

const parseAlertDate = (data: any): Alert => ({
  ...data,
  triggeredAt: new Date(data.triggeredAt),
  acknowledgedAt: data.acknowledgedAt ? new Date(data.acknowledgedAt) : undefined,
  resolvedAt: data.resolvedAt ? new Date(data.resolvedAt) : undefined,
});

const parseOnCallScheduleDate = (data: any): OnCallSchedule => ({
  ...data,
  startTime: new Date(data.startTime),
  endTime: new Date(data.endTime),
});

export const alertsApi = {
  // Alert Rules
  async getAlertRules(): Promise<AlertRule[]> {
    const res = await authorizedFetch('/alerts/rules');
    const data = await json<any[]>(res);
    return data.map(parseAlertRuleDate);
  },

  async createAlertRule(rule: Omit<AlertRule, 'id' | 'createdAt'>): Promise<AlertRule> {
    const res = await authorizedFetch('/alerts/rules', {
      method: 'POST',
      body: JSON.stringify(rule),
    });
    return parseAlertRuleDate(await json(res));
  },

  async updateAlertRule(id: string, updates: Partial<AlertRule>): Promise<AlertRule> {
    const res = await authorizedFetch(`/alerts/rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return parseAlertRuleDate(await json(res));
  },

  async deleteAlertRule(id: string): Promise<void> {
    await authorizedFetch(`/alerts/rules/${id}`, {
      method: 'DELETE',
    });
  },

  // Alerts
  async getAlerts(status?: AlertStatus): Promise<Alert[]> {
    const params = status ? `?status=${status}` : '';
    const res = await authorizedFetch(`/alerts${params}`);
    const data = await json<any[]>(res);
    return data.map(parseAlertDate);
  },

  async acknowledgeAlert(id: string, userId: string): Promise<Alert> {
    const res = await authorizedFetch(`/alerts/${id}/acknowledge`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
    return parseAlertDate(await json(res));
  },

  async resolveAlert(id: string): Promise<Alert> {
    const res = await authorizedFetch(`/alerts/${id}/resolve`, {
      method: 'POST',
    });
    return parseAlertDate(await json(res));
  },

  // On-Call Schedule
  async getOnCallSchedule(): Promise<OnCallSchedule[]> {
    const res = await authorizedFetch('/alerts/oncall');
    const data = await json<any[]>(res);
    return data.map(parseOnCallScheduleDate);
  },

  async createOnCallSchedule(schedule: Omit<OnCallSchedule, 'id'>): Promise<OnCallSchedule> {
    const res = await authorizedFetch('/alerts/oncall', {
      method: 'POST',
      body: JSON.stringify(schedule),
    });
    return parseOnCallScheduleDate(await json(res));
  },

  async updateOnCallSchedule(id: string, updates: Partial<OnCallSchedule>): Promise<OnCallSchedule> {
    const res = await authorizedFetch(`/alerts/oncall/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return parseOnCallScheduleDate(await json(res));
  },

  async deleteOnCallSchedule(id: string): Promise<void> {
    await authorizedFetch(`/alerts/oncall/${id}`, {
      method: 'DELETE',
    });
  },
};
